import { ArrowDown, Compass, Shield, Leaf } from 'lucide-react';
import { motion } from 'motion/react';
import homepagePic from '../assets/HomepagePicNo3.webp';
import { useTranslation } from '../hooks/useTranslation';
import SouthAfricaMap from './SouthAfricaMap';

interface HeroSectionProps {
	onExploreClick: () => void;
	onPlanClick: () => void;
	backgroundUrl?: string;
}

export default function HeroSection({ onExploreClick, onPlanClick, backgroundUrl = homepagePic }: HeroSectionProps) {
	const { t, currentLanguage } = useTranslation();

	// We can write a smooth scroll to have a gorgeous tactile feel
	const handleScrollDown = () => {
		// Scroll past the full screen hero
		const anchor = document.getElementById('travel-styles-anchor');
		if (anchor) {
			anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
		} else {
			onExploreClick();
		}
	};

	return (
		<div
			id='homepage-immersive-hero'
			className='relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-brand-forest-dark'
		>
			{/* Immersive Static Image or High-Res Background - 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1920&q=80' */}
			<div className='absolute inset-0 z-0 select-none'>
				<img
					src={backgroundUrl}
					alt='Majestic Elephant and Savannah Landscape at Sunset'
					className='w-full h-full object-cover object-center scale-100 transition-transform duration-[12s] ease-out-quad'
					referrerPolicy='no-referrer'
					fetchPriority='high'
					loading='eager'
					decoding='async'
				/>
				{/* Cinematic darkened overlays for maximum readability of white typography */}
				<div className='absolute inset-0 bg-linear-to-t from-brand-forest-dark via-brand-forest-dark/45 to-brand-forest-dark/20'></div>
				<div className='absolute inset-0 bg-linear-to-r from-brand-forest-dark/70 via-transparent to-brand-forest-dark/40'></div>

				{/* Soft bottom transition blend specifically to mitigate layout bleed issues */}
				<div className='absolute inset-x-0 bottom-0 h-32 bg-linear-to-t from-earth-cream to-transparent'></div>
			</div>

			{/* Main Floating Design Grid - Content */}
			<div className='relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 pt-10 pb-20 text-center lg:text-left'>
				<div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-center'>
					{/* Headline Column */}
					<div className='lg:col-span-7 space-y-8'>
						{/* Elegant Tagline Pill */}
						{/* <motion.div
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							className='inline-flex items-center space-x-2.5 bg-brand-gold/15 border border-brand-gold/30 rounded-full px-4 py-1.5 backdrop-blur-sm'
						>
							<span className='w-1.5 h-1.5 rounded-full bg-brand-gold animate-ping'></span>
							<span className='font-sans text-[10px] sm:text-xs tracking-[0.25em] text-brand-gold-light uppercase font-bold'>
								{t('experienceWildlife')}
							</span>
						</motion.div> */}

						{/* Editorial Large Elegant Heading */}
						<div className='space-y-4'>
							<motion.h1
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.9, delay: 0.3 }}
								className='font-serif text-4xl sm:text-6xl md:text-7xl lg:text-[4.5rem] xl:text-[5.5rem] text-white font-medium tracking-tight leading-[1.04] text-left'
							>
								{currentLanguage === 'en' && (
									<>
										A Wild Experience <br />
										<span className='italic text-brand-gold font-light block mt-2 font-serif'>
											You Will Never Forget
										</span>
									</>
								)}
								{currentLanguage === 'it' && (
									<>
										Un'Esperienza Selvaggia <br />
										<span className='italic text-brand-gold font-light block mt-2 font-serif'>
											Che Non Dimenticherete Mai
										</span>
									</>
								)}
								{currentLanguage === 'fr' && (
									<>
										Une Expérience Sauvage <br />
										<span className='italic text-brand-gold font-light block mt-2 font-serif'>
											Que Vous N'Oublierez Jamais
										</span>
									</>
								)}
								{currentLanguage === 'de' && (
									<>
										Ein wildes Erlebnis, <br />
										<span className='italic text-brand-gold font-light block mt-2 font-serif'>
											das Sie nie vergessen werden
										</span>
									</>
								)}
							</motion.h1>

							<motion.p
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 1.2, delay: 0.5 }}
								className='font-sans text-xs sm:text-sm md:text-base text-earth-sand-light max-w-2xl leading-relaxed text-left font-light'
							>
								{currentLanguage === 'en' && (
									<>
										At Elephant Expeditions, our goal is to share our passion for African animals and travel by creating
										a seamless, enriching experience that you will never forget. Explore South Africa’s finest luxury
										lodges, charming budget stays, self-drive tours, and spectacular Big 5 safaris.
									</>
								)}
								{currentLanguage === 'it' && (
									<>
										Presso Elephant Expeditions, il nostro obiettivo è condividere con voi la nostra passione per gli
										animali africani e per i viaggi, creando un'esperienza di viaggio fluida e arricchente che NON
										DIMENTICHERETE MAI! Esplorate i migliori lodge di lusso del Sud Africa, i suggestivi soggiorni
										economici, i tour self-drive e gli spettacolari safari alla ricerca dei Big 5.
									</>
								)}
								{currentLanguage === 'fr' && (
									<>
										Chez Elephant Expeditions, notre objectif est de partager notre passion pour les animaux d’Afrique
										et le voyage à travers une expérience fluide et enrichissante que vous N'OUBLIEREZ JAMAIS ! Explorez
										les plus prestigieux lodges de luxe d'Afrique du Sud, des séjours de charme, des circuits en liberté
										et de spectaculaires safaris à la recherche des Big 5.
									</>
								)}
								{currentLanguage === 'de' && (
									<>
										Bei Elephant Expeditions ist es unser Ziel, unsere Leidenschaft für afrikanische Tiere und das
										Reisen mit Ihnen zu teilen. Wir schaffen ein nahtloses, bereicherndes Reiseerlebnis, das Sie NIE
										VERGESSEN WERDEN! Entdecken Sie Südafrikas feinste Luxus-Lodges, charmante Budget-Unterkünfte,
										Selbstfahrer-Touren und spektakuläre Big-5-Safaris.
									</>
								)}
							</motion.p>
						</div>

						{/* Interactive Actions CTA */}
						<motion.div
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, delay: 0.7 }}
							className='flex flex-wrap items-center gap-4 justify-start'
						>
							<button
								id='hero-explore-packages'
								onClick={handleScrollDown}
								className='group relative inline-flex items-center space-x-3 bg-brand-gold hover:bg-white text-brand-forest-dark font-sans text-xs tracking-widest uppercase font-bold px-7 py-4 rounded-lg shadow-xl cursor-pointer transition-all duration-300 ease-in-out active:scale-95 border border-brand-gold'
							>
								<span>{t('curatedTravelStyles')}</span>
								<ArrowDown className='w-3.5 h-3.5 text-brand-forest duration-300 ease-in-out transform group-hover:translate-y-1' />
							</button>

							<button
								id='hero-plan-itinerary'
								onClick={onPlanClick}
								className='inline-flex items-center space-x-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-sans text-xs tracking-widest uppercase font-semibold px-7 py-4 rounded-lg cursor-pointer transition-all duration-300 ease-in-out active:scale-95 backdrop-blur-sm'
							>
								<Compass className='w-4 h-4 text-brand-gold' />
								<span>{t('bespokePlanner')}</span>
							</button>
						</motion.div>

						{/* Micro Sustainability Assurances */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 1.5, delay: 1 }}
							className='grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-white/15 max-w-xl text-left'
						>
							<div className='flex items-center space-x-2'>
								<Leaf className='w-4 h-4 text-brand-gold shrink-0' />
								<span className='font-sans text-[11px] text-earth-sand-light'>{t('ethcialSafaris')}</span>
							</div>
							<div className='flex items-center space-x-2'>
								<Compass className='w-4 h-4 text-brand-gold shrink-0' />
								<span className='font-sans text-[11px] text-earth-sand-light'>{t('conservationFocused')}</span>
							</div>
							<div className='flex items-center space-x-2'>
								<Shield className='w-4 h-4 text-brand-gold shrink-0' />
								<span className='font-sans text-[11px] text-earth-sand-light'>{t('authenticExperiences')}</span>
							</div>
						</motion.div>
					</div>

					{/* South Africa Map Silhouette Column */}
					<div className='lg:col-span-5 flex flex-col items-center lg:items-end justify-center pt-6 lg:pt-0'>
						<SouthAfricaMap />
					</div>
				</div>
			</div>
		</div>
	);
}
